/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import * as d3 from 'd3';

import CircularGraphProps from './type';
import Typography from '../Typography';
import { formatNumberAsCurrency, getUniqueKey } from '../utils';
import {
  LabelsContentWrapperStyled,
  InnerGraphInfoCircleStyled,
  LabelColorContainerStyled,
  GraphContentWrapperStyled,
  LabelContainerStyled,
  WrapperStyled,
} from './styled';

const CircularGraph = ({ ...props }: CircularGraphProps) => {
  const {
    initialLabelType,
    finalLabelType,
    hideLabels,
    data,
    content,
    size,
    labelFontSize,
    labelPadding,
    total,
    innerProps,
    chartId,
  } = props;
  const [totalData, setTotalData] = useState(0);

  useEffect(() => {
    if (!total) {
      setTotalData(data.reduce((acc, { value }) => acc + value, 0));
    } else {
      setTotalData(total);
    }

    const spacing = data.length > 1 ? 0.05 : 0;
    const dataValues = data.map(({ value }) => value);
    const colorMap = data.map(({ color }) => color);

    const width = size;
    const height = size;
    const radius = Math.min(width, height) / 2;

    d3.select(`#${chartId ?? 'donut-chart'}`)
      .selectAll('*')
      .remove();

    const svg = d3
      .select(`#${chartId ?? 'donut-chart'}`)
      .style('position', 'relative')
      .style('width', '100%')
      .style('height', '100%')
      .append('svg')
      .style('position', 'absolute')
      .style('top', '50%')
      .style('left', '50%')
      .style('transform', 'translate(-50%, -50%)')
      .attr('width', width)
      .attr('height', height)
      .style('overflow', 'visible')
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    const arc = d3
      .arc()
      .innerRadius(radius * 0.8)
      .outerRadius(radius * 1)
      .cornerRadius(100);

    const pie = d3.pie().value((d: number) => d);

    const g = svg
      .selectAll('.arc')
      .data(pie(dataValues))
      .enter()
      .append('g')
      .attr('data-testid', (_, i) => `test-group-${i}`)
      .attr('class', 'arc');
    const getDArg = (d: d3.DefaultArcObject, currentArc: d3.Arc<any, d3.DefaultArcObject>) => {
      const startAngle = d.startAngle + spacing;
      const endAngle = d.endAngle - spacing;
      const newArc = d3
        .arc()
        .innerRadius(currentArc.innerRadius())
        .outerRadius(currentArc.outerRadius())
        .startAngle(startAngle)
        .endAngle(endAngle)
        .cornerRadius(100);
      return newArc(d);
    };

    g.append('path')
      .attr('d', (d: any) => getDArg(d, arc))
      .style('fill', (_, i) => colorMap[i])
      .style(
        'filter',
        'drop-shadow(4px 4px 20px rgba(111, 140, 176, 0.41)) drop-shadow(-6px -6px 20px #FFF) drop-shadow(2px 2px 4px rgba(114, 142, 171, 0.10))',
      );

    const largeArcCentroid = (d: any) => {
      const newArc = d3
        .arc()
        .innerRadius(radius * 1.3)
        .outerRadius(radius * 1.2)
        .startAngle(d.startAngle)
        .endAngle(d.endAngle)
        .cornerRadius(100);
      return newArc.centroid(d);
    };

    const labels = g
      .append('foreignObject')
      .attr('class', 'label')
      .attr('data-testid', (_, i) => `test-label-${i}`)
      .style('display', 'none')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '100%')
      .attr('height', '100%')
      .style('position', 'absolute')
      .style('top', '0')
      .style('left', '0')
      .style('z-index', '3')
      .style('pointer-events', 'none')
      .style('transform', 'translate(0%, 0%)')
      .style('overflow', 'visible');

    const labelWrapper = labels
      .append('xhtml:div')
      .style('position', 'absolute')
      .style('top', '50%')
      .style('left', '50%')
      .style('transform', 'translate(-50%, -50%)');

    const labelBox = labelWrapper
      .append('div')
      .style('transform', (d) => {
        return `translate(${largeArcCentroid(d)[0]}px, ${largeArcCentroid(d)[1]}px)`;
      })
      .style('border-radius', '6px')
      .style('border', '1px solid #D6E3F3')
      .style('background', '#E3EDF7')
      .style(
        'box-shadow',
        '4px 4px 20px 0px rgba(111, 140, 176, 0.41), -6px -6px 20px 0px #FFF, 2px 2px 4px 0px rgba(114, 142, 171, 0.10)',
      )
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('width', 'min-content')
      .style('height', 'min-content')
      .style('justify-content', 'center');

    labelBox
      .append('span')
      .style('padding', labelPadding ?? '6px 8px')
      .style('font-size', labelFontSize ?? '13px')
      .style('font-weight', '500')
      .style('color', '#557FC3')
      .style('font-family', 'Poppins')
      .style('line-height', '20px')
      .text(
        (d: any) =>
          `${initialLabelType ?? ''}${formatNumberAsCurrency(d.data)}${finalLabelType ?? ''}`,
      );

    g.on('mouseover', function mouseOverHandler(this: SVGGElement) {
      d3.select(this).select('.label').style('display', 'block');
    }).on('mouseout', function mouseOutHandler(this: SVGGElement) {
      d3.select(this).select('.label').style('display', 'none');
    });
  }, [props]);

  return (
    <WrapperStyled>
      <GraphContentWrapperStyled size={`${size}px`}>
        <div id={`${chartId ?? 'donut-chart'}`} />
        <InnerGraphInfoCircleStyled size={`${size * 0.6}px`}>
          {innerProps?.textPosition === 'top' && (
            <Typography
              style={{
                textAlign: 'center',
                fontSize: `${innerProps?.textSize ?? 16}px`,
                lineHeight: 1.2,
              }}
            >
              {content || ''}
            </Typography>
          )}
          <Typography style={{ fontSize: `${innerProps?.numberSize ?? 30}px` }}>
            {initialLabelType}
            {formatNumberAsCurrency(totalData) || ''}
            {finalLabelType}
          </Typography>
          {innerProps?.textPosition === 'bottom' && (
            <Typography
              style={{
                textAlign: 'center',
                fontSize: `${innerProps?.textSize ?? 16}px`,
                lineHeight: 1.2,
              }}
            >
              {content || ''}
            </Typography>
          )}
        </InnerGraphInfoCircleStyled>
      </GraphContentWrapperStyled>
      {!hideLabels && (
        <LabelsContentWrapperStyled data-testid={'labels-title'} width={`${size}px`}>
          {data.map(({ label, color }) => (
            <LabelContainerStyled key={getUniqueKey()}>
              <LabelColorContainerStyled backgroundColor={color} />
              <Typography color="#2D2F39">{label}</Typography>
            </LabelContainerStyled>
          ))}
        </LabelsContentWrapperStyled>
      )}
    </WrapperStyled>
  );
};

CircularGraph.defaultProps = {
  size: 243,
  hideLabels: false,
  innerProps: {
    textPosition: 'bottom',
    textSize: 20,
    numberSize: 30,
  },
};

export default CircularGraph;
